import React, { useEffect, useState } from 'react';
import { Typography, Card, Alert, Spin, Table, Tag, Button } from 'antd';
import { DollarOutlined } from '@ant-design/icons';
import { authHeader } from '../../../utils/api';

const { Title, Paragraph } = Typography;

const NeighborhoodLens = () => {
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [datasets, setDatasets] = useState([]);
  const [loadingDatasets, setLoadingDatasets] = useState(false);
  const [pricing, setPricing] = useState(null);

  useEffect(() => {
    const checkAccess = async () => {
      try {
        // Check if client has NeighborhoodLens access
        const response = await fetch('/api/clients/me/features', {
          headers: { ...authHeader() }
        });
        
        if (response.ok) {
          const data = await response.json();
          const access = data.neighborhood_lens_enabled && data.is_active;
          setHasAccess(access);
          
          // If has access, fetch datasets and pricing
          if (access) {
            fetchDatasets();
            fetchPricing();
          }
        } else {
          setHasAccess(false);
        }
      } catch (error) {
        console.error('Error checking NeighborhoodLens access:', error);
        setHasAccess(false);
      } finally {
        setLoading(false);
      }
    };

    checkAccess();
  }, []);

  const fetchDatasets = async () => {
    setLoadingDatasets(true);
    try {
      const response = await fetch('/api/audience-datasets/me', {
        headers: { ...authHeader() }
      });
      
      if (response.ok) {
        const data = await response.json();
        setDatasets(data.items || []);
      } else {
        console.error('Failed to fetch datasets');
        setDatasets([]);
      }
    } catch (error) {
      console.error('Error fetching datasets:', error);
      setDatasets([]);
    } finally {
      setLoadingDatasets(false);
    }
  };

  const fetchPricing = async () => {
    try {
      const response = await fetch('/api/clients/me', {
        headers: { ...authHeader() }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPricing(data.pricing_config || null);
      }
    } catch (error) {
      console.error('Error fetching pricing:', error);
    }
  };

  const handlePayToDownload = (dataset) => {
    // Calculate monthly total from pricing
    let amount = 0;
    if (pricing) {
      const basePrice = Number(pricing.basePrice) || 0;
      const nlEnabled = Boolean(pricing.nlEnabled);
      const nlQty = Number(pricing.nlQty) || 0;
      const nlPrice = Number(pricing.nlPrice) || 0;
      const locEnabled = Boolean(pricing.locEnabled);
      const locUnlimited = Boolean(pricing.locUnlimited);
      const locQty = Number(pricing.locQty) || 0;
      const locPrice = Number(pricing.locPrice) || 0;
      const fleetEnabled = Boolean(pricing.fleetEnabled);
      const fleetUnlimited = Boolean(pricing.fleetUnlimited);
      const fleetQty = Number(pricing.fleetQty) || 0;
      const fleetPrice = Number(pricing.fleetPrice) || 0;

      const nlSubtotal = nlEnabled ? nlQty * nlPrice : 0;
      const locSubtotal = locEnabled && !locUnlimited ? locQty * locPrice : 0;
      const fleetSubtotal = fleetEnabled && !fleetUnlimited ? fleetQty * fleetPrice : 0;
      amount = basePrice + nlSubtotal + locSubtotal + fleetSubtotal;
    }

    // Open payment page in new tab with calculated amount
    const paymentUrl = `/client/payment/dataset?dataset_id=${dataset.id}&dataset_name=${encodeURIComponent(dataset.name || 'Dataset')}&amount=${amount.toFixed(2)}`;
    window.open(paymentUrl, '_blank', 'width=800,height=900');
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div style={{ padding: '24px' }}>
        <Alert
          message="Access Denied"
          description="You don't have access to NeighborhoodLens. Please contact your administrator to enable this feature."
          type="warning"
          showIcon
          style={{ marginBottom: '24px' }}
        />
      </div>
    );
  }

  const columns = [
    {
      title: 'Dataset Name',
      dataIndex: 'name',
      key: 'name',
      render: (text) => <strong>{text}</strong>,
    },
    {
      title: 'File',
      dataIndex: 'original_filename',
      key: 'original_filename',
      render: (text) => text || '—',
    },
    {
      title: 'Records',
      dataIndex: 'row_count',
      key: 'row_count',
      render: (count) => <Tag color="blue">{(count || 0).toLocaleString()} rows</Tag>,
    },
    {
      title: 'Uploaded',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => date ? new Date(date).toLocaleDateString() : '—',
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Button
          type="primary"
          icon={<DollarOutlined />}
          size="small"
          onClick={() => handlePayToDownload(record)}
        >
          Pay to Download
        </Button>
      ),
    },
  ];

  const renderPricingCalculator = () => {
    if (!pricing) return null;

    const basePrice = Number(pricing.basePrice) || 0;
    const nlEnabled = Boolean(pricing.nlEnabled);
    const nlQty = Number(pricing.nlQty) || 0;
    const nlPrice = Number(pricing.nlPrice) || 0;
    const locEnabled = Boolean(pricing.locEnabled);
    const locUnlimited = Boolean(pricing.locUnlimited);
    const locQty = Number(pricing.locQty) || 0;
    const locPrice = Number(pricing.locPrice) || 0;
    const fleetEnabled = Boolean(pricing.fleetEnabled);
    const fleetUnlimited = Boolean(pricing.fleetUnlimited);
    const fleetQty = Number(pricing.fleetQty) || 0;
    const fleetPrice = Number(pricing.fleetPrice) || 0;
    const geoUnlimited = Boolean(pricing.geoUnlimited);

    const nlSubtotal = nlEnabled ? nlQty * nlPrice : 0;
    const locSubtotal = locEnabled && !locUnlimited ? locQty * locPrice : 0;
    const fleetSubtotal = fleetEnabled && !fleetUnlimited ? fleetQty * fleetPrice : 0;
    const monthlyTotal = basePrice + nlSubtotal + locSubtotal + fleetSubtotal;

    return (
      <Card title="$ Pricing Calculator" style={{ marginTop: '24px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Base Plan</span>
            <span style={{ fontWeight: 500 }}>${basePrice.toFixed(2)}</span>
          </div>
          
          {nlEnabled && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>NeighborhoodLens</span>
              <span style={{ fontWeight: 500 }}>${nlSubtotal.toFixed(2)}</span>
            </div>
          )}
          
          {locEnabled && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Number of Locations ({locQty})</span>
              <span style={{ fontWeight: 500 }}>{locUnlimited ? 'Unlimited' : `$${locSubtotal.toFixed(2)}`}</span>
            </div>
          )}
          
          {fleetEnabled && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Fleet Size ({fleetQty})</span>
              <span style={{ fontWeight: 500 }}>{fleetUnlimited ? 'Unlimited' : `$${fleetSubtotal.toFixed(2)}`}</span>
            </div>
          )}
          
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Geo Audiences</span>
            <span style={{ fontWeight: 500 }}>{geoUnlimited ? 'Unlimited' : '$0.00'}</span>
          </div>
          
          <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '12px', marginTop: '8px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 600 }}>Monthly Total</span>
              <span style={{ fontWeight: 600, fontSize: '16px' }}>${monthlyTotal.toFixed(2)}</span>
            </div>
          </div>
        </div>
      </Card>
    );
  };

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>NeighborhoodLens_Client</Title>
      <Paragraph>
        Welcome to NeighborhoodLens - your advanced location-based analytics and targeting platform.
      </Paragraph>

      {renderPricingCalculator()}

      <Card 
        title="Your Audience Datasets" 
        style={{ marginTop: '24px' }}
        extra={datasets.length > 0 && <Tag color="green">{datasets.length} dataset{datasets.length !== 1 ? 's' : ''}</Tag>}
      >
        {loadingDatasets ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
            <Spin />
          </div>
        ) : datasets.length === 0 ? (
          <Alert
            message="No Datasets Yet"
            description="Your administrator hasn't uploaded any NeighborhoodLens datasets for your account yet. Contact your admin to get started."
            type="info"
            showIcon
          />
        ) : (
          <Table
            columns={columns}
            dataSource={datasets}
            rowKey="id"
            pagination={false}
          />
        )}
      </Card>

      <div style={{ display: 'grid', gap: '16px', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', marginTop: '24px' }}>
        <Card title="Location Analytics" hoverable>
          <Paragraph>
            Analyze foot traffic patterns, demographic insights, and competitor analysis for your target locations.
          </Paragraph>
        </Card>

        <Card title="Audience Insights" hoverable>
          <Paragraph>
            Discover detailed audience profiles based on location data, including shopping patterns and preferences.
          </Paragraph>
        </Card>

        <Card title="Campaign Optimization" hoverable>
          <Paragraph>
            Optimize your advertising campaigns using location-based insights and real-time performance data.
          </Paragraph>
        </Card>

        <Card title="Competitive Intelligence" hoverable>
          <Paragraph>
            Monitor competitor activity and market share in your target neighborhoods and locations.
          </Paragraph>
        </Card>
      </div>
    </div>
  );
};

export default NeighborhoodLens;
